import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useContactBooking } from '../../contexts/ContactBookingContext';
const CAL_EMBED_URL = 'https://cal.com/shreyaspawar/intro?embed=true';
const CAL_ORIGIN = 'https://cal.com';
const BOOKING_CONFIRMED_SIGNALS = ['bookingSuccessful', 'booking_successful', '__closeIframe'];
export function CalEmbedModal() {
  const {
    isModalOpen,
    modalSource,
    lead,
    closeModal,
    confirmBooking
  } = useContactBooking();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const embedUrl = useMemo(() => {
    if (modalSource === 'prefill' && lead) {
      const params = new URLSearchParams({
        name: lead.name,
        email: lead.email,
        notes: `${lead.company}\n\n${lead.message}`
      });
      return `${CAL_EMBED_URL}&${params.toString()}`;
    }
    return CAL_EMBED_URL;
  }, [modalSource, lead]);
  useEffect(() => {
    if (!isModalOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, closeModal]);
  useEffect(() => {
    function handleBookingConfirmed() {
      confirmBooking();
      window.setTimeout(() => {
        closeModal();
      }, 1500);
    }
    function handleMessage(event: MessageEvent) {
      if (event.origin !== CAL_ORIGIN) return;
      if (!isModalOpen) return;
      const data = event.data;
      const signal = typeof data === 'object' && data !== null ? data.type ?? data.action : undefined;
      if (typeof signal === 'string' && BOOKING_CONFIRMED_SIGNALS.includes(signal)) {
        handleBookingConfirmed();
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isModalOpen, confirmBooking, closeModal]);
  const handleManualConfirm = () => {
    confirmBooking();
    window.setTimeout(() => {
      closeModal();
    }, 1500);
  };
  return createPortal(<AnimatePresence data-id="element-251" data-mp="2ewi8sx2oktaq">
      {isModalOpen && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: prefersReducedMotion ? 0 : 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-252" data-mp="2jpl1z43jq0qe">
          <div aria-hidden="true" onClick={closeModal} className="absolute inset-0 bg-ink/60" data-id="element-253" data-mp="1w0s3p0zgbgsm" />

          <motion.div role="dialog" aria-modal="true" aria-label="Book a 30-minute intro call" initial={prefersReducedMotion ? false : {
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={prefersReducedMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative flex h-[88vh] w-full max-w-[760px] flex-col overflow-hidden rounded-card bg-white" data-id="element-254" data-mp="210wkks49udjd">
            <button ref={closeButtonRef} type="button" onClick={closeModal} aria-label="Close booking calendar" className="absolute right-4 top-4 z-10 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-hairline bg-white text-ink outline-none transition-colors duration-150 ease-out hover:border-ink focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2" data-id="element-255" data-mp="1vbroo5vcu8qr">
              <XIcon size={16} aria-hidden="true" data-id="element-256" data-mp="cd28pn4xeygu" />
            </button>

            <iframe key={embedUrl} src={embedUrl} title="Book a 30-minute intro call" className="h-full w-full flex-1 border-0" data-id="element-257" data-mp="hcydholg6txg" />

            <button type="button" onClick={handleManualConfirm} className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 cursor-pointer rounded-full bg-white/90 px-3 py-1 text-[13px] text-muted underline decoration-muted/40 underline-offset-4 outline-none transition-colors duration-150 ease-out hover:text-ink focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2" data-id="element-258" data-mp="q5a172p6g39q">
              I&rsquo;ve finished booking
            </button>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}